import { h } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import { getBundleProducts } from 'utils/storefront/get-bundle-products';
import Image from 'utils/image';
import setAttributes from 'utils/set-attributes';
import { Play } from 'utils/icon/video-icons';
import ProductPrice from './product-price';
import ProductBadge from '../product/product-badge';

// Global state to track currently playing video
let currentlyPlayingVideo = null;
let lastHoveredCard = null;
// Track which videos are available in each carousel
const carouselVideosMap = {};
// Flag to track if we have an autoplay video on initial load
let hasAutoplayedInitialVideo = false;
// Store intervals for cleanup
const carouselCheckIntervals = {};

// Custom hook for carousel video management
const useCarouselVideo = (
  product,
  hasVideo,
  videoRef,
  cardRef,
  carouselId,
  index,
  isDesktop,
  isTabletOrDesktop,
  isVisible,
  posterImageLoaded,
  setIsVideoPlaying
) => {
  const [shouldAutoplay, setShouldAutoplay] = useState(false);
  
  // Use a ref for tracking state that doesn't need to trigger re-renders
  const videoState = useRef({
    isRegistered: false,
    hasBeenPlayed: false,
    isCurrentlyActive: false
  });
  
  // Combine carousel registration and visibility detection in one effect
  useEffect(() => {
    if (!cardRef.current || !hasVideo) return undefined;
    
    // Register this card as having a video
    if (!carouselVideosMap[carouselId]) {
      carouselVideosMap[carouselId] = { 
        videos: [],
        initialized: false,
        lastScrollPosition: 0,
        activeVideoHandle: null
      };
    }
    
    // Only add if not already tracked
    if (!videoState.current.isRegistered) {
      carouselVideosMap[carouselId].videos.push({
        handle: product.handle,
        index,
        videoRef,
        cardRef
      });
      videoState.current.isRegistered = true;
    }
    
    // Setup visibility detection with IntersectionObserver
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // Handle desktop initial autoplay behavior
            if (isTabletOrDesktop) {
              const carouselData = carouselVideosMap[carouselId];
              if (carouselData && carouselData.videos.length > 0) {
                // Sort videos by index to find those closest to the beginning
                const sortedVideos = [...carouselData.videos].sort((a, b) => a.index - b.index);
                const visibleVideos = sortedVideos.filter(v => {
                  const rect = v.cardRef.current.getBoundingClientRect();
                  return rect.left >= 0 && rect.right <= window.innerWidth;
                });
                
                // Get first video by index and check if it's this one
                const isFirstVideo = sortedVideos[0]?.handle === product.handle;
                // Check if this should be the autoplay video (first visible one)
                const shouldBeAutoplay = !hasAutoplayedInitialVideo && 
                  visibleVideos.length > 0 && 
                  visibleVideos[0].handle === product.handle;
                
                // Autoplay this video if it's the first visible one or the first overall with no other playing
                if (shouldBeAutoplay || (isFirstVideo && !currentlyPlayingVideo)) {
                  // Stop any currently playing video
                  if (currentlyPlayingVideo && currentlyPlayingVideo !== videoRef.current) {
                    currentlyPlayingVideo.pause();
                  }
                  
                  videoRef.current.play().catch(process.env.NODE_ENV !== 'production' ? 
                    // eslint-disable-next-line no-console
                    (e) => console.error('Video play error:', e) : 
                    () => {});
                  setIsVideoPlaying(true);
                  currentlyPlayingVideo = videoRef.current;
                  
                  if (shouldBeAutoplay) {
                    setShouldAutoplay(true);
                    hasAutoplayedInitialVideo = true;
                  }
                }
              }
            }
          }
        });
      },
      { threshold: 0.6 } // Card is considered visible when 60% is in view
    );
    
    observer.observe(cardRef.current);
    
    // Cleanup function
    return () => {
      // Remove this card from the registry
      if (carouselVideosMap[carouselId]) {
        carouselVideosMap[carouselId].videos = carouselVideosMap[carouselId].videos.filter(
          item => !(item.handle === product.handle && item.index === index)
        );
        
        // If this was the last card, clean up the interval
        if (carouselVideosMap[carouselId].videos.length === 0) {
          clearInterval(carouselCheckIntervals[carouselId]);
          delete carouselCheckIntervals[carouselId];
          carouselVideosMap[carouselId].initialized = false;
        }
      }
      
      // Remove the method from video element
      if (videoRef.current) {
        const videoElement = videoRef.current;
        delete videoElement._setIsPlaying;
      }
      
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
      
      videoState.current.isRegistered = false;
    };
  }, [cardRef, hasVideo, product.handle, index, carouselId, isTabletOrDesktop, videoRef, setIsVideoPlaying]);
  
  // Setup mobile carousel video management - kept separate due to complexity
  useEffect(() => {
    if (!hasVideo || !cardRef.current || isTabletOrDesktop) return undefined;
    
    // Find the Swiper container
    const findSwiperContainer = () => {
      let element = cardRef.current.parentElement;
      while (element) {
        if (element.classList.contains('swiper-slide')) {
          // Found slide, now get container
          return element.parentElement.parentElement;
        }
        if (!element || element === document.body) return null;
        element = element.parentElement;
      }
      return null;
    };
    
    const swiperContainer = findSwiperContainer();
    if (!swiperContainer) return undefined;
    
    // Get the Swiper instance
    const { swiper } = swiperContainer;
    
    // Global function to check all videos in this carousel and play the leftmost one
    const checkAndUpdateCarouselVideos = () => {
      const carouselData = carouselVideosMap[carouselId];
      if (!carouselData || carouselData.videos.length === 0) return;
      
      // Find all videos that are at least 30% visible
      const visibleVideos = carouselData.videos.filter(v => {
        if (!v.cardRef.current || !v.videoRef.current) return false;
        const rect = v.cardRef.current.getBoundingClientRect();
        
        // Consider a card visible if at least 30% of it is in the viewport
        const visibleWidth = Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0);
        const percentVisible = visibleWidth / rect.width;
        return percentVisible >= 0.3;
      });
      
      if (visibleVideos.length === 0) return;
      
      // Sort by leftmost position
      const sortedVideos = visibleVideos.sort((a, b) => {
        const rectA = a.cardRef.current.getBoundingClientRect();
        const rectB = b.cardRef.current.getBoundingClientRect();
        return rectA.left - rectB.left;
      });
      
      // Get the leftmost visible video
      const leftmostVideo = sortedVideos[0];
      
      // If there's no change in the active video, do nothing
      if (carouselData.activeVideoHandle === leftmostVideo.handle) return;
      
      // Update the active video handle
      carouselData.activeVideoHandle = leftmostVideo.handle;
      
      // Pause any currently playing video
      if (currentlyPlayingVideo) {
        currentlyPlayingVideo.pause();
      }
      
      // Play the leftmost video
      const videoToPlay = leftmostVideo.videoRef.current;
      videoToPlay.play().catch(() => {});
      currentlyPlayingVideo = videoToPlay;
      
      // Update all card components about their playing state
      carouselData.videos.forEach(v => {
        if (v.handle === leftmostVideo.handle) {
          // This is reactive - will trigger a rerender on the component that owns this ref
          if (v.videoRef.current._setIsPlaying) {
            v.videoRef.current._setIsPlaying(true);
          }
        } else if (v.videoRef.current._setIsPlaying) {
          v.videoRef.current._setIsPlaying(false);
        }
      });
    };
    
    // Store method on video element to update its playing state
    if (videoRef.current) {
      const videoElement = videoRef.current;
      videoElement._setIsPlaying = (isPlaying) => {
        setIsVideoPlaying(isPlaying);
      };
    }
    
    // Set up continuous checking interval for this carousel (if not already set)
    if (!carouselCheckIntervals[carouselId]) {
      // Reduced interval frequency from 200ms to 500ms for better performance
      carouselCheckIntervals[carouselId] = setInterval(checkAndUpdateCarouselVideos, 500);
    }
    
    // Event handlers with useCallback would be better, but keeping original structure for now
    const handleTransitionEnd = () => {
      checkAndUpdateCarouselVideos();
    };
    
    // Add event listeners to Swiper
    if (swiper) {
      swiper.on('slideChangeTransitionEnd', handleTransitionEnd);
      swiper.on('touchEnd', handleTransitionEnd);
      swiper.on('resize', checkAndUpdateCarouselVideos);
    }
    
    // Also add direct DOM events to catch all possible interactions
    swiperContainer.addEventListener('scroll', checkAndUpdateCarouselVideos);
    swiperContainer.addEventListener('touchend', checkAndUpdateCarouselVideos);
    
    // Initial check for videos
    if (isVisible) {
      setTimeout(checkAndUpdateCarouselVideos, 300);
    }
    
    // Clean up event listeners and intervals
    return () => {
      if (swiper) {
        swiper.off('slideChangeTransitionEnd', handleTransitionEnd);
        swiper.off('touchEnd', handleTransitionEnd);
        swiper.off('resize', checkAndUpdateCarouselVideos);
      }
      
      swiperContainer.removeEventListener('scroll', checkAndUpdateCarouselVideos);
      swiperContainer.removeEventListener('touchend', checkAndUpdateCarouselVideos);
    };
  }, [
    hasVideo, 
    isTabletOrDesktop, 
    carouselId, 
    product.handle, 
    index, 
    isVisible, 
    videoRef, 
    cardRef, 
    setIsVideoPlaying
  ]);
  
  // Combine mobile initial video autoplay and window resize into one effect
  useEffect(() => {
    if (!hasVideo || !videoRef.current) return undefined;
    
    // For mobile, initial video handling
    if (!isTabletOrDesktop) {
      // Get carousel data
      const carouselData = carouselVideosMap[carouselId];
      if (!carouselData || carouselData.videos.length === 0) return undefined;
      
      // Only initialize once per carousel and when poster image is loaded
      if (!carouselData.initialized && carouselData.videos.length > 0 && posterImageLoaded) {
        // Mark as initialized
        carouselData.initialized = true;
        
        // Find visible videos
        const visibleVideos = carouselData.videos.filter(v => {
          if (!v.cardRef.current) return false;
          const rect = v.cardRef.current.getBoundingClientRect();
          const visibleWidth = Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0);
          const percentVisible = visibleWidth / rect.width;
          return percentVisible >= 0.3;
        }).sort((a, b) => {
          const rectA = a.cardRef.current.getBoundingClientRect();
          const rectB = b.cardRef.current.getBoundingClientRect();
          return rectA.left - rectB.left;
        });
        
        // Check if this is the first visible video
        if (visibleVideos.length > 0 && 
            visibleVideos[0].handle === product.handle && 
            isVisible && 
            !hasAutoplayedInitialVideo) {
          
          // Update the active video handle
          carouselData.activeVideoHandle = product.handle;
          
          // Play this video
          videoRef.current.play().catch(() => {});
          setIsVideoPlaying(true);
          setShouldAutoplay(true);
          currentlyPlayingVideo = videoRef.current;
          hasAutoplayedInitialVideo = true;
        }
      }
    }
    
    // Handle window resize and visibility changes
    const handleResize = () => {
      // Check if this video is currently playing
      const wasPlaying = currentlyPlayingVideo === videoRef.current && !videoRef.current.paused;
      
      // Get the current viewport width
      const currentIsDesktop = window.innerWidth >= 1024;
      const currentIsTabletOrDesktop = window.innerWidth >= 768;
      const currentIsMobile = window.innerWidth < 768;
      
      // If we've changed between any breakpoint mode
      if (currentIsDesktop !== isDesktop || 
          currentIsTabletOrDesktop !== isTabletOrDesktop ||
          (currentIsMobile && window.innerWidth !== window.oldWidth)) {
        
        // Store current width for future comparison
        window.oldWidth = window.innerWidth;
        
        // If switching to mobile mode, reset the carousel's initialization
        if (!currentIsTabletOrDesktop && carouselVideosMap[carouselId]) {
          carouselVideosMap[carouselId].initialized = false;
        }
        
        // Ensure the video continues playing regardless of breakpoint change
        if (wasPlaying && videoRef.current) {
          // Small timeout to let the resize operation complete
          setTimeout(() => {
            videoRef.current.play().catch(() => {});
            setIsVideoPlaying(true);
            currentlyPlayingVideo = videoRef.current;
          }, 50);
        }
      }
    };
    
    // Handle page visibility changes
    const handleVisibilityChange = () => {
      // Make sure we keep videos playing when switching between pages or tabs
      if (currentlyPlayingVideo === videoRef.current && 
          !document.hidden && videoRef.current.paused) {
        videoRef.current.play().catch(() => {});
        setIsVideoPlaying(true);
      }
    };
    
    window.addEventListener('resize', handleResize);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [
    hasVideo, 
    isDesktop, 
    isTabletOrDesktop,
    carouselId, 
    product.handle, 
    isVisible, 
    posterImageLoaded, 
    videoRef, 
    setIsVideoPlaying
  ]);
  
  return {
    shouldAutoplay
  };
};

const CustomProductCard = ({
  product,
  useBuilderFunctionality,
  showMobileAltImage = false,
  isDesktop,
  index,
  trackingData,
  // eslint-disable-next-line no-unused-vars
  carouselRef,
  carouselId = 'default'
}) => {
  const [isAdded, setIsAdded] = useState(false);
  const [bundle, setBundle] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [isVisible, setIsVisible] = useState(false);
  const [posterImageLoaded, setPosterImageLoaded] = useState(false);
  const url = `${Shopify.routes.root}products/${product.handle}`;
  const indicatorRef = useRef(null);
  const firstCardRef = useRef(null);
  const cardRef = useRef(null);
  const videoRef = useRef(null);
  const technicalDataEl = document.querySelector('[data-technical]');
  const data = JSON.parse(technicalDataEl.textContent);
  
  // Add isTabletOrDesktop state for tablets (768px-1024px) and desktop (>= 1024px)
  const isTabletOrDesktop = isDesktop || (typeof window !== 'undefined' && window.innerWidth >= 768);
  
  // Update the hasVideo check to validate URLs properly
  const hasVideo = product.custom_product_card_video && 
                  product.custom_product_card_video_poster_image && 
                  !product.custom_product_card_video.includes('/?') && 
                  !product.custom_product_card_video_poster_image.includes('/?') &&
                  product.raw_product_card_video && 
                  product.raw_product_card_video_poster_image;

  // Use custom hook for video management
  const { shouldAutoplay } = useCarouselVideo(
    product,
    hasVideo,
    videoRef,
    cardRef,
    carouselId,
    index,
    isDesktop,
    isTabletOrDesktop,
    isVisible,
    posterImageLoaded,
    setIsVideoPlaying
  );

  useEffect(() => {
    const isBundle = product.tags?.find((tag) => tag === 'bundle');
    if (isBundle) {
      const fetchData = async () => {
        setBundle(await getBundleProducts(product));
      };
      // eslint-disable-next-line no-console
      fetchData().catch(process.env.NODE_ENV !== 'production' ? console.error : () => {});
    }
  }, []);

  // Initialize Yotpo widgets and animate first card if needed
  useEffect(() => {
    if (window.yotpoWidgetsContainer?.initWidgets) {
      window.yotpoWidgetsContainer.initWidgets();
    }
    if (!isDesktop && showMobileAltImage && firstCardRef.current) {
      setTimeout(() => {
        firstCardRef.current.classList.add('animateCard');
      }, 1000);
    }
  }, []);

  // Set initial load complete after a short delay - optimized for performance
  useEffect(() => {
    // For first video, we need more careful handling
    if (hasVideo) {
      // Use a shorter timer for better performance
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 100); // Reduced from 300ms to minimize delay when poster is already loaded
      return () => clearTimeout(timer);
    }
    
    // For non-videos, set immediately without a re-render cycle
    setIsVisible(true);
    return undefined;
  // Empty dependency array ensures this only runs once on mount
  }, [hasVideo]);

  // Handle video playback for desktop hover
  useEffect(() => {
    if (!videoRef.current || !hasVideo) return undefined;

    // Updated to use isTabletOrDesktop instead of isDesktop for tablet support
    if (isTabletOrDesktop && isHovered) {
      // Stop any currently playing video
      if (currentlyPlayingVideo && currentlyPlayingVideo !== videoRef.current) {
        currentlyPlayingVideo.pause();
      }
      
      videoRef.current.play().catch(process.env.NODE_ENV !== 'production' ? 
        // eslint-disable-next-line no-console
        (e) => console.error('Video play error:', e) : 
        () => {});
      setIsVideoPlaying(true);
      currentlyPlayingVideo = videoRef.current;
      lastHoveredCard = cardRef.current;
    } else if (isTabletOrDesktop && !isHovered && lastHoveredCard !== cardRef.current) {
      // Only pause if another card is being hovered and we're in tablet/desktop mode
      videoRef.current.pause();
      setIsVideoPlaying(false);
    }
    
    return undefined;
  }, [isHovered, hasVideo, isTabletOrDesktop, cardRef]);

  // Handle hover state changes
  const handleMouseEnter = () => {
    if (isTabletOrDesktop) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    if (isTabletOrDesktop) {
      setIsHovered(false);
    }
  };

  const hasGiftCardTag = (tags) => {
    const hasTag = tags && tags.includes('gift_card');
    return !!hasTag;
  };

  const handleClick = (e) => {
    if (!trackingData) return;
    e.preventDefault();
    /* eslint-disable no-param-reassign */
    trackingData = {
      ...trackingData,
      destinationPage: `${window.location.href}${url}`,
      contentText: `${product.title} - ${product.variants[0]?.sku}`,
    };
    /* eslint-enable no-param-reassign */

    Shopify.analytics.publish('home_content_click', trackingData);
    window.location.href = url;
  };

  const handleAddToCart = (e, hasUrl) => {
    e.preventDefault();

    if (hasUrl) {
      window.location.href = url;
      return;
    }

    let productPayload;
    if (bundle) {
      productPayload = window.getBundlePayload(bundle, product, product.variants[0], 1);
    } else {
      const attributes = setAttributes(product);
      productPayload = {
        ...window.getPayload(product.variants[0], 1),
        attributes,
      };
    }

    const addToCartEvent = new CustomEvent('cart:add', {
      detail: {
        payload: productPayload,
      },
    });
    document.dispatchEvent(addToCartEvent);
    setIsAdded(true);

    setTimeout(() => {
      setIsAdded(false);
    }, 1000);
  };

  const handleScroll = (e) => {
    if (isTabletOrDesktop || !showMobileAltImage) return;
    const position = e.target.scrollLeft;
    indicatorRef.current.style.transform = `translateX(${Math.ceil((position * 70) / e.target.offsetWidth)}%)`;
  };

  const isProductAvailable = bundle
    ? bundle.available
    : product.variants && product.variants.length > 0
    ? product.variants[0].availableForSale || product.variants[0].available
    : false;

  return (
    <article
      className="relative product-card flex flex-col h-full p-px overflow-hidden"
      ref={cardRef}
      data-handle={`${product.handle}`}
      data-has-video={hasVideo}
    >
      <a
        className="relative product-card-container group block no-underline overflow-hidden"
        href={url}
        ref={index === 0 ? firstCardRef : null}
        aria-label={`${product.title}`}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={() => isTabletOrDesktop && setIsHovered(true)}
        onBlur={() => isTabletOrDesktop && setIsHovered(false)}
      >
        <div
          className={`relative mb-3 aspect-[163/186] lg:aspect-[317/346] ${
            showMobileAltImage && !isTabletOrDesktop
              ? 'flex overflow-x-auto lg:static scrollbar-hide snap-mandatory snap-x'
              : ''
          }`}
          onScroll={(e) => handleScroll(e)}
        >
          {hasVideo ? (
            <span className="absolute inset-0 w-full h-full">
              <ProductBadge
                tags={product.tags}
                className={'nosto-badge bg-pantone-8 absolute z-10 flex items-center text-nowrap left-[4px] top-[4px]'}
              />
              <img 
                src={product.custom_product_card_video_poster_image}
                alt={`${product.title} video poster`}
                className={`absolute left-0 top-0 w-full h-full object-cover opacity-100 transition-opacity duration-500 ${isVideoPlaying ? 'opacity-0' : ''}`}
                onLoad={() => setPosterImageLoaded(true)}
              />
              {/* Always render play button but control visibility with CSS */}
              <div 
                className={`absolute inset-0 flex items-center justify-center 
                pointer-events-none z-5 play-button-container 
                ${isVideoPlaying ? 'opacity-0' : posterImageLoaded ? 'opacity-100' : 'opacity-0'} 
                transition-opacity duration-300`}
              >
                <div className="w-10 h-10 flex items-center justify-center bg-black rounded-full text-white">
                  <Play className="w-5 h-5 ml-0.5" />
                </div>
              </div>
              <video
                ref={videoRef}
                src={product.custom_product_card_video}
                poster={product.custom_product_card_video_poster_image}
                muted
                playsInline
                preload="metadata"
                className={`absolute left-0 top-0 w-full h-full object-cover transition-opacity duration-500 ${isVideoPlaying ? 'opacity-100' : 'opacity-0'}`}
                loop
                autoPlay={shouldAutoplay}
                onPlaying={() => {
                  setIsVideoPlaying(true);
                }}
                onPause={() => {
                  // Always show play button when video is paused, regardless of visibility
                  setIsVideoPlaying(false);
                }}
              />
            </span>
          ) : (product.custom_product_image_src && !product.custom_product_image_src.includes('no-image')) ||
          product.images[0] ? (
            <span
              className={`
            ${
              showMobileAltImage && !isTabletOrDesktop
                ? 'min-w-full h-full opacity-100 relative snap-start'
                : product.images[1] && isTabletOrDesktop
                ? 'opacity-100 group-hover:opacity-0 transition-opacity duration-500'
                : 'opacity-100'
            }`}
            >
              <ProductBadge
                tags={product.tags}
                className={'nosto-badge bg-pantone-8 absolute z-10 flex items-center text-nowrap left-[4px] top-[4px]'}
              />
              <Image
                {...(product.custom_product_image_src && !product.custom_product_image_src.includes('no-image')
                  ? {
                      src: product.custom_product_image_src,
                      alt: 'Custom Product Image',
                      isCustomMetafieldImage: true, // Set the prop to true for custom metafield images
                    }
                  : product.images[0])}
                srcWidths={{ sm: 640 }}
                classes="absolute left-0 top-0 w-full h-full object-cover"
              />
            </span>
          ) : null}
          
          {/* Show second image on hover for products without videos */}
          {!hasVideo && product.images[1] && isTabletOrDesktop ? (
            <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-1">
              <Image
                {...product.images[1]}
                srcWidths={{ sm: 640 }}
                classes="absolute left-0 top-0 w-full h-full object-cover"
                loading="lazy"
              />
            </span>
          ) : null}
        </div>
        {showMobileAltImage && product.images.length > 1 && !isTabletOrDesktop && (
          <div
            className="absolute h-[3px] w-5 bg-grey-4 left-2.5 
                        bottom-5 rounded-lg overflow-hidden"
          >
            <span className="indicator block h-[3px] w-3 bg-black rounded-lg" ref={indicatorRef} />
          </div>
        )}
      </a>
      <div className="flex flex-col justify-between flex-1 z-2 relative">
        <div className="w-full relative product-card-container pb-[20px]">
          <a className="group block leading-5 hover:underline" href={url} onClick={handleClick}>
            <h3 className="text-headline-tag">
              {product.custom_product_title ? product.custom_product_title : product.title}
            </h3>

            {product.subtitle && (
              <div
                className="text-body-light"
                dangerouslySetInnerHTML={{
                  __html: product.custom_product_description ? product.custom_product_description : product.subtitle,
                }}
              ></div>
            )}
          </a>

          <p className="text-body-2 mb-2.5">
            <ProductPrice product={product} />
          </p>

          {/* Ensure the 'product.last_purchased_date' string is formatted as "MMM DD, YYYY" */}
          {product.last_purchased_date && (
            <div className="text-sm mt-1">Last Purchased on {product.last_purchased_date}</div>
          )}

          <div>
            <div
              className="yotpo-widget-instance"
              data-yotpo-instance-id={data.yotpoBottomlineId}
              data-yotpo-product-id={product.id.split('Product/')[1]}
              data-yotpo-cart-product-id={product.id.split('Product/')[1]}
              data-yotpo-section-id="collection"
            ></div>

            {product.custom_review_text && (
              <div className="text-black text-body-light-2">
                <div dangerouslySetInnerHTML={{ __html: product.custom_review_text }}></div>
              </div>
            )}
          </div>
        </div>

        <button
          className={`button button--secondary w-full
            ${!isProductAvailable ? 'pointer-events-none' : ''} `}
          disabled={!isProductAvailable}
          onClick={(e) => handleAddToCart(e, hasGiftCardTag(product.tags))}
        >
          {hasGiftCardTag(product.tags)
            ? window.TMW.Strings.product.viewDetails
            : isAdded
            ? window.TMW.Strings.product.added
            : !isProductAvailable
            ? window.TMW.Strings.product.outOfStock
            : window.TMW.Strings.product.addToCart}
        </button>
        {useBuilderFunctionality && (
          <div>
            <div className="flex items-center border border-black p-2 w-max mt-2.5 lg:mt-5">
              <button aria-label="Remove" className="w-[27px] h-[27px] text-center text-black font-bold">
                −
              </button>
              <input
                type="number"
                max="20"
                value="1"
                className="w-[25px] h-[27px] px-2 border-0 text-center text-black"
              />
              <button aria-label="Add" className="w-[27px] h-[27px] text-center text-black font-bold">
                +
              </button>
            </div>
            <button
              className={`button button--secondary mt-2.5 w-full 
                py-3.5 lg:py-[22px] lg:mt-[22px]
              ${!isProductAvailable ? 'pointer-events-none' : ''} `}
            >
              {isAdded
                ? window.TMW.Strings.product.added
                : !isProductAvailable
                ? window.TMW.Strings.product.outOfStock
                : 'add'}
            </button>
          </div>
        )}
      </div>
    </article>
  );
};

export default CustomProductCard;
